import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Edit2, X, Users, UserPlus } from 'lucide-react';

export const AdminCommunitiesTab = ({
  communities, openCommunityEdit, editingCommunity, setEditingCommunity,
  communityEditForm, setCommunityEditForm, handleSaveCommunity,
  handleDeleteCommunity, communityMembers, communityLoading,
  memberSearch, handleSearchMembers, memberSearchResults,
  handleAddMember, handleRemoveMember, setMemberSearch, setMemberSearchResults
}) => (
  <>
    <Card>
      <CardHeader>
        <CardTitle>Communautés ({communities.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Communauté</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground">Localisation</th>
                <th className="text-center py-3 px-4 font-medium text-muted-foreground">Membres</th>
                <th className="text-right py-3 px-4 font-medium text-muted-foreground">Prix kWh</th>
                <th className="text-right py-3 px-4 font-medium text-muted-foreground">Énergie</th>
                <th className="text-center py-3 px-4 font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {communities.map((c, i) => (
                <tr key={i} className="border-b hover:bg-muted/50">
                  <td className="py-3 px-4"><p className="font-medium">{c.name}</p></td>
                  <td className="py-3 px-4 text-muted-foreground">{c.city || c.location}, {c.postal_code || ''}</td>
                  <td className="py-3 px-4 text-center"><Badge variant="outline">{c.member_count || 0}</Badge></td>
                  <td className="py-3 px-4 text-right font-medium">{c.price_per_kwh?.toFixed(2) || '0.15'}€</td>
                  <td className="py-3 px-4 text-right">{c.total_energy_kwh?.toFixed(1) || 0} kWh</td>
                  <td className="py-3 px-4 text-center">
                    <Button size="sm" variant="outline" onClick={() => openCommunityEdit(c)} data-testid={`edit-community-${i}`}>
                      <Edit2 className="w-4 h-4 mr-1" /> Gérer
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>

    {editingCommunity && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="community-edit-modal">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b">
            <h3 className="text-xl font-bold">Gérer la communauté</h3>
            <button onClick={() => { setEditingCommunity(null); setMemberSearch(''); setMemberSearchResults([]); }} className="p-2 hover:bg-muted rounded-lg" data-testid="close-community-modal">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground">Informations</h4>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Nom de la communauté</Label>
                  <Input value={communityEditForm.name} onChange={(e) => setCommunityEditForm(f => ({ ...f, name: e.target.value }))} data-testid="community-name-input" />
                </div>
                <div>
                  <Label>Prix par kWh (€)</Label>
                  <Input type="number" step="0.01" min="0" value={communityEditForm.price_per_kwh} onChange={(e) => setCommunityEditForm(f => ({ ...f, price_per_kwh: parseFloat(e.target.value) || 0 }))} data-testid="community-price-input" />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <textarea
                  value={communityEditForm.description}
                  onChange={(e) => setCommunityEditForm(f => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="community-description-input"
                />
              </div>
              <h4 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground pt-2">Localisation</h4>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><Label>Ville</Label><Input value={communityEditForm.city} onChange={(e) => setCommunityEditForm(f => ({ ...f, city: e.target.value }))} data-testid="community-city-input" /></div>
                <div><Label>Département</Label><Input value={communityEditForm.department} onChange={(e) => setCommunityEditForm(f => ({ ...f, department: e.target.value }))} data-testid="community-department-input" /></div>
                <div><Label>Code postal</Label><Input value={communityEditForm.postal_code} onChange={(e) => setCommunityEditForm(f => ({ ...f, postal_code: e.target.value }))} data-testid="community-postal-input" /></div>
              </div>
              <div className="flex items-center justify-between pt-2">
                <Button onClick={handleSaveCommunity} className="btn-primary" data-testid="save-community-btn">Sauvegarder les modifications</Button>
                <Button variant="destructive" onClick={() => handleDeleteCommunity(editingCommunity.id, editingCommunity.name)} data-testid="delete-community-btn">Supprimer la communauté</Button>
              </div>
            </div>

            <div className="space-y-4 border-t pt-4">
              <h4 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground">Membres ({communityMembers.length})</h4>
              <div className="relative">
                <Label>Ajouter un membre</Label>
                <div className="flex gap-2">
                  <Input placeholder="Rechercher un utilisateur par nom ou email..." value={memberSearch} onChange={(e) => handleSearchMembers(e.target.value)} data-testid="member-search-input" />
                </div>
                {memberSearchResults.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border rounded-xl shadow-lg z-10 max-h-48 overflow-y-auto" data-testid="member-search-results">
                    {memberSearchResults.map((u) => (
                      <div key={u.id} className="flex items-center justify-between p-3 hover:bg-muted/50 border-b last:border-0">
                        <div>
                          <p className="font-medium text-sm">{u.name}</p>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        </div>
                        <Button size="sm" onClick={() => handleAddMember(u.id)} data-testid={`add-member-${u.id}`}>
                          <UserPlus className="w-4 h-4 mr-1" /> Ajouter
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {communityLoading ? (
                <div className="text-center py-4 text-muted-foreground">Chargement...</div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {communityMembers.map((m) => (
                    <div key={m.user_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-xl" data-testid={`member-row-${m.user_id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Users className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{m.user?.name || m.user_name}</p>
                          <p className="text-xs text-muted-foreground">{m.user?.email || ''} · {m.user?.role || m.user_type}</p>
                        </div>
                      </div>
                      <Button size="sm" variant="destructive" onClick={() => handleRemoveMember(m.user_id)} data-testid={`remove-member-${m.user_id}`}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {communityMembers.length === 0 && (
                    <p className="text-center py-4 text-muted-foreground text-sm">Aucun membre dans cette communauté</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )}
  </>
);
